/**
 * Guest "contact on file" identity proof (issue #277).
 *
 * Self check-in acts on one guest, and every guest of the gallery can reach it, so the API wants
 * proof that the person is that guest: the email or phone the couple stored for them, which the
 * public roster never shows. The API refuses a missing or wrong proof with these codes. The UI
 * shows the API's own message (friendly text) and never the raw code.
 *
 * - CONTACT_REQUIRED / CONTACT_MISMATCH: 403, nothing on file / wrong contact.
 * - TOO_MANY_ATTEMPTS: 429 with `Retry-After` (seconds), the guest is locked out for a while.
 * - PROOF_CHECK_UNAVAILABLE: 503, the attempt counter could not be reached, so the API did not
 *   check the contact at all. Trying again shortly is safe.
 */

/** API error code for "the attempt counter could not be reached, so the contact was not checked". */
export const PROOF_CHECK_UNAVAILABLE_CODE = 'PROOF_CHECK_UNAVAILABLE';

export const CONTACT_PROOF_FALLBACK_MESSAGES = {
  CONTACT_REQUIRED: 'Please ask the couple to update this for you.',
  CONTACT_MISMATCH: 'That email or phone does not match our records.',
  TOO_MANY_ATTEMPTS: 'Too many attempts. Please wait a few minutes and try again.',
  [PROOF_CHECK_UNAVAILABLE_CODE]: "We couldn't check your details right now. Please try again in a moment.",
};

/** Shown before any request when the guest has not typed a contact yet. */
export const CONTACT_PROMPT_MESSAGE = 'Enter the email or phone you gave the couple.';

function readHeader(headers, name) {
  if (!headers || typeof headers !== 'object') return undefined;
  if (typeof headers.get === 'function') {
    const value = headers.get(name);
    if (value != null) return value;
  }
  const key = Object.keys(headers).find((candidate) => candidate.toLowerCase() === name);
  return key === undefined ? undefined : headers[key];
}

/**
 * Whole minutes to wait, rounded up, from a readable `Retry-After` in seconds; null when the
 * header is missing (the browser hides it unless the API exposes it through CORS) or is not a
 * positive number of seconds (an HTTP date is not supported).
 */
function retryAfterMinutes(error) {
  const raw = readHeader(error?.response?.headers, 'retry-after');
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  const text = String(raw).trim();
  if (!/^\d+$/.test(text)) return null;
  const seconds = Number(text);
  return seconds > 0 ? Math.ceil(seconds / 60) : null;
}

/**
 * @param {unknown} error axios-style error from a guest check-in request
 * @returns {string | null} friendly text for a contact-proof refusal, or null for any other error
 */
export function getContactProofErrorMessage(error) {
  const data = error?.response?.data;
  const code = data && typeof data === 'object' ? data.error : null;
  if (typeof code !== 'string' || !Object.prototype.hasOwnProperty.call(CONTACT_PROOF_FALLBACK_MESSAGES, code)) {
    return null;
  }
  if (code === 'TOO_MANY_ATTEMPTS') {
    const minutes = retryAfterMinutes(error);
    if (minutes) {
      return `Too many attempts. Please wait ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} and try again.`;
    }
  }
  const message = typeof data.message === 'string' ? data.message.trim() : '';
  return message || CONTACT_PROOF_FALLBACK_MESSAGES[code];
}
